import { inventoryApi, isDomainInactive, type Domain } from '@/services/inventory-api.service';
import type {
  ReferenceResponseData,
  ReferenceResortDomain,
} from '@/store/book-ski-pass.store';

export function collectUniqueReferenceDomainIds(refData: ReferenceResponseData | null): number[] {
  if (!refData) return [];
  const ids = new Set<number>();
  if (refData.priority_domain?.id != null) ids.add(refData.priority_domain.id);
  for (const d of refData.domains ?? []) ids.add(d.id);
  return [...ids];
}

export function stripDomainIdsFromReferenceData(
  data: ReferenceResponseData,
  removeIds: ReadonlySet<number>
): ReferenceResponseData {
  if (removeIds.size === 0) return data;
  const nextDomains = data.domains?.filter((d) => !removeIds.has(d.id));
  const priority =
    data.priority_domain != null && removeIds.has(data.priority_domain.id)
      ? undefined
      : data.priority_domain;
  return {
    ...data,
    domains: nextDomains?.length ? nextDomains : undefined,
    priority_domain: priority,
  };
}

export function stripIdsFromResortDomains(
  list: ReferenceResortDomain[] | null,
  removeIds: ReadonlySet<number>
): ReferenceResortDomain[] | null {
  if (!list?.length) return list;
  const next = list.filter((d) => !removeIds.has(d.id));
  return next.length > 0 ? next : null;
}

export function pruneActiveDomainMap(
  map: Map<number, Domain>,
  refData: ReferenceResponseData | null
): void {
  const keep = new Set(collectUniqueReferenceDomainIds(refData));
  for (const id of [...map.keys()]) {
    if (!keep.has(id)) map.delete(id);
  }
}

async function loadDomainWithActivityCheck(domainId: number): Promise<{
  domain: Domain | null;
  inactive: boolean;
}> {
  try {
    const full = await inventoryApi.getDomainById(domainId, true);
    if (isDomainInactive(full)) return { domain: null, inactive: true };
    return { domain: full, inactive: false };
  } catch {
    return { domain: null, inactive: true };
  }
}

export async function analyzeReferenceDomainActivity(refData: ReferenceResponseData): Promise<{
  inactiveIds: number[];
  activeById: Map<number, Domain>;
}> {
  const ids = collectUniqueReferenceDomainIds(refData);
  const inactiveIds: number[] = [];
  const activeById = new Map<number, Domain>();
  await Promise.all(
    ids.map(async (id) => {
      const { domain, inactive } = await loadDomainWithActivityCheck(id);
      if (inactive) inactiveIds.push(id);
      else if (domain) activeById.set(id, domain);
    })
  );
  return { inactiveIds, activeById };
}

export async function filterResortDomainEntriesToActive(
  domainEntries: Array<{ id: number; name: string }>,
  priority?: { id: number; name: string }
): Promise<{
  activeEntries: Array<{ id: number; name: string }>;
  inactiveIds: number[];
  activeById: Map<number, Domain>;
}> {
  const ids = new Set<number>();
  for (const d of domainEntries) ids.add(d.id);
  if (priority?.id != null) ids.add(priority.id);
  if (ids.size === 0) {
    return { activeEntries: [], inactiveIds: [], activeById: new Map() };
  }

  const inactiveIds: number[] = [];
  const activeById = new Map<number, Domain>();
  await Promise.all(
    [...ids].map(async (id) => {
      const { domain, inactive } = await loadDomainWithActivityCheck(id);
      if (inactive) inactiveIds.push(id);
      else if (domain) activeById.set(id, domain);
    })
  );

  const activeKeys = new Set(activeById.keys());
  const activeEntries = domainEntries.filter((d) => activeKeys.has(d.id));
  return { activeEntries, inactiveIds, activeById };
}
